import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";

interface Block { id: number; page: string; slug: string; title: string; content: string; is_active: number; sort_order: number }

export default function AdminContent() {
  const [data, setData] = useState<Block[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<Block | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get<{ data: Block[] }>("/content");
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || "Failed to load content");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(id: number) {
    if (!window.confirm("Delete this content block?")) return;
    try {
      await api.del(`/content/${id}`);
      load();
    } catch (e: any) {
      alert(e?.message || "Delete failed");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-600">Manage page content blocks shown across the website.</p>
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700">
          <i className="ri-add-line"></i> New Block
        </button>
      </div>

      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}

      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-3">Page</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading && (<tr><td colSpan={5} className="px-4 py-10 text-center text-foreground-600"><i className="ri-loader-4-line animate-spin"></i> Loading...</td></tr>)}
              {!loading && data.map((b) => (
                <tr key={b.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3 capitalize text-foreground-800">{b.page}</td>
                  <td className="px-4 py-3 text-foreground-700">{b.slug}</td>
                  <td className="px-4 py-3 font-medium text-foreground-950">{b.title || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${b.is_active ? "bg-primary-100 text-primary-900" : "bg-background-200 text-foreground-700"}`}>
                      {b.is_active ? "Active" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(b)} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Edit"><i className="ri-pencil-line"></i></button>
                      <button onClick={() => remove(b.id)} className="flex items-center justify-center w-8 h-8 rounded-lg text-accent-700 hover:bg-accent-100" aria-label="Delete"><i className="ri-delete-bin-line"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data.length === 0 && (<tr><td colSpan={5} className="px-4 py-12 text-center text-foreground-600">No content blocks yet.</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {(editing || createOpen) && (
        <BlockEditor
          block={editing}
          onClose={() => { setEditing(null); setCreateOpen(false); }}
          onSaved={() => { setEditing(null); setCreateOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function BlockEditor({ block, onClose, onSaved }: { block: Block | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    page: block?.page || "home",
    slug: block?.slug || "",
    title: block?.title || "",
    content: block?.content || "",
    is_active: block?.is_active ?? 1,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (block) await api.put(`/content/${block.id}`, form);
      else await api.post("/content", form);
      onSaved();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-3xl bg-background-50 border border-background-200 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100 sticky top-0 bg-background-50">
          <h3 className="font-heading text-lg text-foreground-950">{block ? "Edit Content Block" : "New Content Block"}</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close"><i className="ri-close-line"></i></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Page</label>
              <input value={form.page} onChange={(e) => setForm((f) => ({ ...f, page: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Slug *</label>
              <input required value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Title</label>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Content</label>
            <textarea rows={6} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"></textarea>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked ? 1 : 0 }))} />
            <label className="text-sm text-foreground-800">Active (visible on site)</label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}